// Service for Bybit API calls via Edge Function proxy

import { isSupabaseConfigured } from '../lib/supabaseClient';

interface BybitV5Ticker {
  symbol: string;
  lastPrice: string;
  indexPrice: string;
  markPrice: string;
  prevPrice24h: string;
  price24hPcnt: string;
  highPrice24h: string;
  lowPrice24h: string;
  volume24h: string;
  turnover24h: string;
  openInterest: string;
  openInterestValue: string;
  fundingRate: string;
  nextFundingTime: string;
  bid1Price: string;
  bid1Size: string;
  ask1Price: string;
  ask1Size: string;
}

interface BybitV5OrderBookEntry {
  price: string;
  size: string;
}

interface BybitV5OrderBook {
  s: string; // symbol
  b: [string, string][]; // bids [price, size]
  a: [string, string][]; // asks [price, size]
  ts: number; // timestamp
  u: number; // update id
}

interface BybitV5Trade {
  i: string; // trade id
  p: string; // price
  v: string; // volume
  S: string; // side (Buy/Sell)
  T: number; // timestamp
  BT: boolean; // is block trade
}

class BybitService {
  private async callBybitProxy(endpoint: string, params?: Record<string, any>): Promise<any> {
    try {
      // Validate environment variables first
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!isSupabaseConfigured()) {
        console.warn('Supabase environment variables not configured, using fallback data');
        throw new Error('Supabase not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/bybit-proxy`;
      
      // Add retry logic with exponential backoff
      const maxRetries = 3;
      let lastError: Error | null = null;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // Reduced to 10 second timeout
          
          const response = await fetch(supabaseFunctionsUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              endpoint,
              params
            }),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.warn(`Bybit proxy error (attempt ${attempt}): ${response.status} - ${errorText}`);
            
            // If it's a 5xx error or network error, retry. If it's a 4xx error, don't retry
            if ((response.status >= 500 || response.status === 0) && attempt < maxRetries) {
              lastError = new Error(`Bybit proxy error: ${response.status} - ${errorText}`);
              await this.delay(Math.pow(2, attempt) * 1000);
              continue;
            }
            
            throw new Error(`Bybit proxy error: ${response.status} - ${errorText}`);
          }

          const data = await response.json();
          return data;
          
        } catch (error) {
          lastError = error as Error;
          
          if (error.name === 'AbortError') {
            console.warn(`Request timeout (10s) on attempt ${attempt} for endpoint: ${endpoint}`);
          } else if (error instanceof TypeError && error.message.includes('fetch')) {
            console.warn(`Network error on attempt ${attempt} for endpoint ${endpoint}:`, error.message);
          } else {
            console.warn(`Error on attempt ${attempt} for endpoint ${endpoint}:`, error);
          }
          
          // If this is the last attempt, break and use fallback
          if (attempt === maxRetries) {
            break;
          }
          
          // Wait before retrying with exponential backoff
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
      
      // If all attempts failed, throw the last error to trigger fallback
      throw lastError || new Error('All retry attempts failed');
    } catch (error) {
      console.warn('Error calling Bybit proxy, will use fallback data:', error);
      throw error;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get current price for a symbol
  async getPrice(symbol: string): Promise<number> {
    try {
      const data = await this.callBybitProxy('/v5/market/tickers', { 
        category: 'linear',
        symbol 
      });
      
      if (data && data.retCode === 0 && data.result && data.result.list && data.result.list.length > 0) {
        return parseFloat(data.result.list[0].lastPrice);
      }
      
      throw new Error(`No price data available for ${symbol}`);
    } catch (error) {
      console.warn(`Error getting price for ${symbol}, using fallback:`, error);
      // Return fallback price instead of throwing
      return this.getFallbackPrice(symbol);
    }
  }

  // Get ticker data for a symbol or all symbols
  async getTicker(symbol?: string): Promise<BybitV5Ticker[]> {
    try {
      const data = await this.callBybitProxy('/v5/market/tickers', { 
        category: 'linear',
        symbol 
      });
      
      if (data && data.retCode === 0 && data.result && data.result.list) {
        return data.result.list;
      }
      
      throw new Error('Failed to get ticker data');
    } catch (error) {
      console.warn('Error getting ticker data, using fallback:', error);
      // Return fallback tickers instead of throwing
      return this.getFallbackTickers();
    }
  }

  // Get order book data (depth)
  async getOrderBook(symbol: string, limit: number = 25): Promise<BybitV5OrderBook | null> {
    try {
      const data = await this.callBybitProxy('/v5/market/orderbook', { 
        category: 'linear',
        symbol,
        limit
      });
      
      if (data && data.retCode === 0 && data.result) {
        return data.result;
      }
      
      throw new Error(`Failed to get order book for ${symbol}`);
    } catch (error) {
      console.warn(`Error getting order book for ${symbol}, returning null:`, error);
      return null;
    }
  }

  // Get recent trades
  async getRecentTrades(symbol: string, limit: number = 50): Promise<BybitV5Trade[]> {
    try {
      const data = await this.callBybitProxy('/v5/market/trades', { 
        category: 'linear',
        symbol,
        limit
      });
      
      if (data && data.retCode === 0 && data.result && data.result.list) {
        return data.result.list;
      }
      
      throw new Error(`Failed to get recent trades for ${symbol}`);
    } catch (error) {
      console.warn(`Error getting recent trades for ${symbol}, returning empty array:`, error);
      return [];
    }
  }

  // Get all tickers
  async getAllTickers(): Promise<BybitV5Ticker[]> {
    try {
      const data = await this.callBybitProxy('/v5/market/tickers', { 
        category: 'linear'
      });
      
      if (data && data.retCode === 0 && data.result && data.result.list) {
        return data.result.list;
      }
      
      throw new Error('Failed to get all tickers');
    } catch (error) {
      console.warn('Error getting all tickers, using fallback:', error);
      // Return fallback tickers instead of throwing
      return this.getFallbackTickers();
    }
  }

  // Convert Bybit ticker to MarketData format
  convertTickerToMarketData(ticker: BybitV5Ticker): {
    symbol: string;
    price: number;
    volume_24h: number;
    change_24h: number;
    timestamp: string;
    high_price_24h: number;
    low_price_24h: number;
    market_cap: number;
    funding_rate: number;
    open_interest: number;
  } {
    return {
      symbol: ticker.symbol,
      price: parseFloat(ticker.lastPrice),
      volume_24h: parseFloat(ticker.volume24h),
      change_24h: parseFloat(ticker.price24hPcnt) * 100, // Convert to percentage
      timestamp: new Date().toISOString(),
      high_price_24h: parseFloat(ticker.highPrice24h),
      low_price_24h: parseFloat(ticker.lowPrice24h),
      market_cap: 0, // Bybit doesn't provide market cap, will be populated from CoinGecko data
      funding_rate: parseFloat(ticker.fundingRate) * 100, // Convert to percentage
      open_interest: parseFloat(ticker.openInterestValue)
    };
  }

  // Fallback price data for when API fails - returns reasonable fallback
  private getFallbackPrice(symbol: string): number {
    console.warn(`Using fallback price for ${symbol}`);
    
    // Return reasonable fallback prices for common symbols
    const fallbackPrices: Record<string, number> = {
      'BTCUSDT': 107714,
      'ETHUSDT': 2500,
      'ADAUSDT': 0.5,
      'SOLUSDT': 100,
      'DOTUSDT': 7,
      'LINKUSDT': 15,
      'AVAXUSDT': 35,
      'MATICUSDT': 0.8,
      'ATOMUSDT': 10,
      'NEARUSDT': 5
    };
    
    return fallbackPrices[symbol] || 1;
  }

  // Fallback ticker data for when API fails - returns basic ticker structure
  private getFallbackTickers(): BybitV5Ticker[] {
    console.warn('Using fallback ticker data');
    
    const fallbackSymbols = ['BTCUSDT', 'ETHUSDT', 'ADAUSDT', 'SOLUSDT', 'DOTUSDT'];
    const fallbackPrices: Record<string, number> = {
      'BTCUSDT': 107714,
      'ETHUSDT': 2500,
      'ADAUSDT': 0.5,
      'SOLUSDT': 100,
      'DOTUSDT': 7
    };
    
    return fallbackSymbols.map(symbol => ({
      symbol,
      lastPrice: fallbackPrices[symbol]?.toString() || '1',
      indexPrice: fallbackPrices[symbol]?.toString() || '1',
      markPrice: fallbackPrices[symbol]?.toString() || '1',
      prevPrice24h: (fallbackPrices[symbol] * 0.98)?.toString() || '0.98',
      price24hPcnt: '0.02',
      highPrice24h: (fallbackPrices[symbol] * 1.05)?.toString() || '1.05',
      lowPrice24h: (fallbackPrices[symbol] * 0.95)?.toString() || '0.95',
      volume24h: '1000000',
      turnover24h: (fallbackPrices[symbol] * 1000000)?.toString() || '1000000',
      openInterest: '500000',
      openInterestValue: (fallbackPrices[symbol] * 500000)?.toString() || '500000',
      fundingRate: '0.0001',
      nextFundingTime: (Date.now() + 8 * 60 * 60 * 1000).toString(),
      bid1Price: (fallbackPrices[symbol] * 0.999)?.toString() || '0.999',
      bid1Size: '100',
      ask1Price: (fallbackPrices[symbol] * 1.001)?.toString() || '1.001',
      ask1Size: '100'
    }));
  }
}

export const bybitService = new BybitService();
export default BybitService;